// src/config/media.ts
// Mirrors MAJENGO-FRONT/src/config/media.ts — keeps image resolution logic in sync

const CDN_BASE_URL = 'https://media.majengo.co.ke';

/**
 * Resolves a raw media key or URL into a full displayable URL.
 * - Full https:// URLs are returned as-is (handles legacy Supabase URLs too)
 * - R2 object keys (e.g. "products/abc.jpg") are prefixed with the CDN base
 */
export const getImageUrl = (key: string | null | undefined): string => {
  if (!key || typeof key !== 'string') return '';

  // Clean up legacy broken URLs where .env inline comment was baked in (e.g. "https://media.majengo.co.ke  # optional/...")
  let cleaned = key.trim().replace(/\s+#[^/]*/g, '');

  // Already a full URL — return as-is
  if (cleaned.startsWith('http')) return cleaned;

  // Local file path — skip
  if (cleaned.startsWith('file://') || cleaned.includes('/cache/') || cleaned.includes('/data/user/')) return '';

  // R2 object key — prepend CDN base
  return `${CDN_BASE_URL}/${cleaned}`;
};

/**
 * Extracts image URLs from the various shapes media_urls can arrive in,
 * then resolves each key through getImageUrl.
 */
export const extractImageUrls = (mediaUrls: unknown): string[] => {
  if (!mediaUrls) return [];

  try {
    let keys: string[] = [];

    if (typeof mediaUrls === 'string' && mediaUrls.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(mediaUrls);
        if (Array.isArray(parsed)) {
          keys = parsed.filter((u) => typeof u === 'string' && u.length > 0);
        }
      } catch (e) {
        // Fallback to treating it as a single string below
      }
    }

    if (keys.length === 0) {
      if (Array.isArray(mediaUrls)) {
        keys = mediaUrls.filter((u) => typeof u === 'string' && u.length > 0);
      } else if (typeof mediaUrls === 'object' && mediaUrls !== null) {
        const obj = mediaUrls as Record<string, unknown>;
        if (Array.isArray(obj.items)) {
          keys = (obj.items as any[])
            .filter((i) => i?.url && typeof i.url === 'string')
            .map((i) => i.url as string);
        }
      } else if (typeof mediaUrls === 'string' && mediaUrls.length > 0) {
        keys = [mediaUrls];
      }
    }

    return keys.map(getImageUrl).filter(Boolean);
  } catch {
    return [];
  }
};
